import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
    LayoutDashboard,
    ReceiptText,
    ChevronLeft,
    ChevronRight,
    Settings,
    LogOut,
    Zap,
    Users,
    Database
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from '@/components/ui/tooltip';

interface SidebarItemProps {
    icon: React.ElementType;
    label: string;
    href: string;
    isCollapsed: boolean;
    active: boolean;
}

const SidebarItem = ({ icon: Icon, label, href, isCollapsed, active }: SidebarItemProps) => {
    const content = (
        <NavLink
            to={href}
            className={cn(
                "flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 group mb-1",
                active
                    ? "bg-primary text-primary-foreground shadow-md"
                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
            )}
        >
            <Icon className={cn("h-5 w-5 shrink-0", active ? "text-white" : "text-gray-500 group-hover:text-gray-900")} />
            {!isCollapsed && <span className="text-sm font-medium">{label}</span>}
        </NavLink>
    );

    if (isCollapsed) {
        return (
            <TooltipProvider delayDuration={0}>
                <Tooltip>
                    <TooltipTrigger asChild>
                        {content}
                    </TooltipTrigger>
                    <TooltipContent side="right" className="font-medium">
                        {label}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    }

    return content;
};

interface StaffSidebarProps {
    isCollapsed: boolean;
    setIsCollapsed: (collapsed: boolean) => void;
}

const StaffSidebar = ({ isCollapsed, setIsCollapsed }: StaffSidebarProps) => {
    const location = useLocation();

    const navItems = [
        { icon: LayoutDashboard, label: 'Dashboard', href: '/lms/staff/dashboard' },
        { icon: ReceiptText, label: 'Transactions', href: '/lms/staff/transactions' },
        { icon: Database, label: 'Data Explorer', href: '/lms/staff/data-explorer' },
        { icon: Zap, label: 'Simulation', href: '/lms/staff/simulation' },
    ];

    return (
        <aside
            className={cn(
                "bg-white border-r border-gray-200 h-screen sticky top-0 transition-all duration-300 ease-in-out flex flex-col z-20",
                isCollapsed ? "w-16" : "w-64"
            )}
        >
            {/* Header / Logo */}
            <div className={cn(
                "h-16 flex items-center border-b border-gray-100",
                isCollapsed ? "justify-center" : "px-6 justify-between"
            )}>
                {!isCollapsed && <span className="font-bold text-xl text-primary tracking-tight">Intellend</span>}
                {isCollapsed && <div className="w-8 h-8 bg-primary rounded-md flex items-center justify-center text-white font-bold text-lg">I</div>}

                {!isCollapsed && (
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setIsCollapsed(true)}
                        className="h-8 w-8 text-gray-500 hover:text-gray-900"
                    >
                        <ChevronLeft className="h-5 w-5" />
                    </Button>
                )}
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-3 overflow-y-auto overflow-x-hidden">
                <div className={cn("mb-2 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-widest", isCollapsed && "sr-only")}>
                    Main Menu
                </div>
                {navItems.map((item) => (
                    <SidebarItem
                        key={item.href}
                        {...item}
                        isCollapsed={isCollapsed}
                        active={location.pathname === item.href}
                    />
                ))}
            </nav>

            {/* Footer / Collapse Toggle for Small Screens or Bottom */}
            <div className="p-3 border-t border-gray-100">
                {isCollapsed && (
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setIsCollapsed(false)}
                        className="h-8 w-8 w-full text-gray-500 hover:text-gray-900"
                    >
                        <ChevronRight className="h-5 w-5" />
                    </Button>
                )}

                {/* Optional: User profile or logout here if not in top bar */}
            </div>
        </aside>
    );
};

export default StaffSidebar;
